import React, { useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Z } from '../../styles/zLayers';
import { useRedditDestinations } from '../../hooks/useRedditDestinations';
import {
  DropdownPosition,
  dropdownWrapperStyle,
  positionDropdown,
} from '../../utils/dropdownPosition';
import { useFollowAnchor } from '../../hooks/useFollowAnchor';

const MENU_WIDTH = 220;
const ROW_ESTIMATE = 38;

/**
 * Reddit destination picker for the tended-card row (PlacementControl's
 * sibling): frost trigger showing where this post goes — the connected
 * profile (u/username, the null default) or a validated subreddit from
 * the brand's growing list. The menu footer adds new subreddits inline:
 * server-validated via Ayrshare (nonexistent/private names rejected with
 * the reason), flair options cached on the entry. When the chosen
 * destination has flairs, a second trigger picks one.
 */
const RedditDestinationControl: React.FC<{
  brandId: number;
  value: string | null;
  flairId: string | null;
  onChange: (destination: string | null) => void;
  onFlairChange: (flairId: string | null) => void;
  /** The record of a published post: subreddit and flair show as labels, no menus. */
  disabled?: boolean;
}> = ({ brandId, value, flairId, onChange, onFlairChange, disabled = false }) => {
  const reddit = useRedditDestinations(brandId);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const flairTriggerRef = useRef<HTMLButtonElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const flairPortalRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<DropdownPosition | null>(null);
  const [flairPos, setFlairPos] = useState<DropdownPosition | null>(null);
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const profileLabel = reddit.username ? `u/${reddit.username}` : 'Profile';
  const selectedEntry = value
    ? reddit.destinations.find((d) => d.name.toLowerCase() === value.toLowerCase())
    : undefined;
  const label = value
    ? (value.startsWith('u_') ? `u/${value.slice(2)}` : `r/${value}`)
    : profileLabel;

  const flairs = selectedEntry?.flairs || [];
  const selectedFlair = flairs.find((f) => f.id === flairId);

  const destinationCount = reddit.destinations.length;
  const flairCount = flairs.length;
  const place = useCallback(() => {
    const anchor = triggerRef.current?.getBoundingClientRect();
    if (!anchor) return;
    setPos(positionDropdown(anchor, MENU_WIDTH, (destinationCount + 3) * ROW_ESTIMATE));
  }, [destinationCount]);
  const open = () => {
    setError(null);
    place();
  };
  const placeFlair = useCallback(() => {
    const anchor = flairTriggerRef.current?.getBoundingClientRect();
    if (!anchor) return;
    setFlairPos(positionDropdown(anchor, MENU_WIDTH, (flairCount + 2) * ROW_ESTIMATE));
  }, [flairCount]);
  const openFlair = placeFlair;
  const close = useCallback(() => setPos(null), []);
  const closeFlair = useCallback(() => setFlairPos(null), []);
  // Follow the triggers on scroll; close when covered or off-screen.
  useFollowAnchor(pos !== null, triggerRef, place, close, portalRef);
  useFollowAnchor(flairPos !== null, flairTriggerRef, placeFlair, closeFlair, flairPortalRef);

  const pick = (destination: string | null) => {
    setPos(null);
    if (destination !== value) {
      onChange(destination);
      onFlairChange(null); // flair belongs to the previous subreddit
    }
  };

  const addSubreddit = async () => {
    const name = draft.trim();
    if (!name || adding) return;
    setAdding(true);
    setError(null);
    try {
      const entry = await reddit.add(name);
      setDraft('');
      pick(entry.name);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add subreddit');
    } finally {
      setAdding(false);
    }
  };

  const triggerClass = (isOpen: boolean) =>
    `flex items-center gap-1 rounded-md border border-white/60 px-2 py-1 text-xs font-medium text-gray-700 transition-colors ${
      isOpen ? 'bg-white/70' : 'bg-white/45 hover:bg-white/65'
    }`;

  const chevron = (
    <svg className="h-3 w-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );

  if (disabled) {
    const locked = 'flex items-center rounded-md border border-white/60 bg-white/45 px-2 py-1 text-xs font-medium text-gray-700';
    return (
      <>
        <span className={locked} title="Subreddit" data-testid="reddit-destination-locked">
          <span className="truncate max-w-[110px]">{label}</span>
        </span>
        {selectedFlair && (
          <span className={locked} title="Flair">
            <span className="truncate max-w-[90px]">{selectedFlair.text}</span>
          </span>
        )}
      </>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (pos ? setPos(null) : open())}
        className={triggerClass(!!pos)}
        title="Subreddit"
      >
        <span className="truncate max-w-[110px]">{label}</span>
        {chevron}
      </button>

      {flairs.length > 0 && (
        <button
          ref={flairTriggerRef}
          type="button"
          onClick={() => (flairPos ? setFlairPos(null) : openFlair())}
          className={triggerClass(!!flairPos)}
          title="Flair"
        >
          <span className="truncate max-w-[90px]">{selectedFlair?.text || 'Flair'}</span>
          {chevron}
        </button>
      )}

      {pos &&
        createPortal(
          <div ref={portalRef}>
            <div style={{ position: 'fixed', inset: 0, zIndex: Z.ABOVE_ITEM }} onClick={() => setPos(null)} />
            <div style={dropdownWrapperStyle(pos, 1000001)}>
              <div className="tend-menu tend-surface" role="menu" style={{ width: MENU_WIDTH, fontSize: 13 }}>
                <button
                  type="button"
                  className={`tend-menu-item${!value ? ' is-active' : ''}`}
                  onClick={() => pick(null)}
                >
                  {profileLabel}
                </button>
                {reddit.destinations.map((d) => (
                  <button
                    key={d.name}
                    type="button"
                    className={`tend-menu-item${value?.toLowerCase() === d.name.toLowerCase() ? ' is-active' : ''}`}
                    onClick={() => pick(d.name)}
                  >
                    <span className="flex-1 truncate text-left">r/{d.name}</span>
                    {d.flairs.length > 0 && (
                      <span style={{ opacity: 0.5, fontSize: 11 }}>flair</span>
                    )}
                  </button>
                ))}
                {/* Footer: validated inline add */}
                <div style={{ padding: '6px 8px 4px', borderTop: '1px solid rgba(255,255,255,.12)' }}>
                  <div className="flex items-center gap-1">
                    <span className="tend-micro" style={{ opacity: 0.6 }}>r/</span>
                    <input
                      className="tend-ink-field flex-1 text-xs"
                      placeholder="add subreddit"
                      value={draft}
                      disabled={adding}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') addSubreddit(); }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <button
                      type="button"
                      className="tend-micro"
                      style={{ opacity: adding ? 0.4 : 0.8 }}
                      disabled={adding}
                      onClick={addSubreddit}
                    >
                      {adding ? '…' : '＋'}
                    </button>
                  </div>
                  {error && (
                    <span className="tend-micro" style={{ color: '#f87171', textTransform: 'none', letterSpacing: 0.3 }}>
                      {error}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {flairPos &&
        createPortal(
          <div ref={flairPortalRef}>
            <div style={{ position: 'fixed', inset: 0, zIndex: Z.ABOVE_ITEM }} onClick={() => setFlairPos(null)} />
            <div style={dropdownWrapperStyle(flairPos, 1000001)}>
              <div className="tend-menu tend-surface" role="menu" style={{ width: MENU_WIDTH, fontSize: 13 }}>
                <button
                  type="button"
                  className={`tend-menu-item${!flairId ? ' is-active' : ''}`}
                  onClick={() => { setFlairPos(null); onFlairChange(null); }}
                >
                  No flair
                </button>
                {flairs.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className={`tend-menu-item${flairId === f.id ? ' is-active' : ''}`}
                    onClick={() => { setFlairPos(null); onFlairChange(f.id); }}
                  >
                    {f.text}
                  </button>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default RedditDestinationControl;
